import {
  Button,
  Card,
  Divider,
  Overlay2,
  Tag,
  Tooltip,
} from '@blueprintjs/core';
import { email } from '@axo/shared/data-access/types';
import { Icon } from '@blueprintjs/core';
import { CreatedAtTag, OrttoEmailIcon } from '../CommunicationItem';
import cardClasses from '../CommunicationItem/Card.module.css';
import {
  email_communication_hooks,
  useCustomer,
} from '@axo/shared/data-access/hooks';
import { useState } from 'react';
import styles from './OrttoEmailCard.module.css';
import { formatDatetime } from '../../../utils/formatDate.ts';

interface IOrttoEmailCard {
  email: email.OrttoEmail;
}

const EmailHTMLPreview = ({ email }: { email: email.OrttoEmail }) => {
  const HTMLPreview = email_communication_hooks.useOrttoEmailHTML(
    email.EmailHTMLID,
  );
  return (
    <div className={styles.emailPreviewInner}>
      {HTMLPreview.isLoading && <>Loading...</>}
      {HTMLPreview.isError && <>Error loading email preview</>}
      {HTMLPreview.isSuccess && (
        <>
          <h2>{HTMLPreview.data?.Subject}</h2>
          <Divider />
          <iframe
            sandbox=""
            title="email preview"
            style={{
              width: '100%',
              height: '70dvh',
            }}
            srcDoc={HTMLPreview.data?.HTML ?? ''}
          />
        </>
      )}
    </div>
  );
};

const StatusTag = ({
  status,
  timestamp,
}: {
  status: email.OrttoEmailStatus;
  timestamp: string;
}) => {
  const getIntent = () => {
    switch (status) {
      case email.OrttoEmailStatus.Sent:
        return 'warning';
      case email.OrttoEmailStatus.Received:
        return 'primary';
      case email.OrttoEmailStatus.Opened:
        return 'success';
      case email.OrttoEmailStatus.Clicked:
        return 'success';
      case email.OrttoEmailStatus.Bounced:
        return 'danger';
      default:
        return 'none';
    }
  };

  return (
    <Tooltip position={'bottom'} content={formatDatetime(timestamp)}>
      <Tag intent={getIntent()} minimal>
        {status}
      </Tag>
    </Tooltip>
  );
};

export function OrttoEmailCard({ email }: IOrttoEmailCard) {
  const customer = useCustomer(email.CustomerID);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  return (
    <Card className={cardClasses.card}>
      <div className={cardClasses.topBar}>
        <OrttoEmailIcon />
        <Tag round minimal>
          Ortto
        </Tag>
        <CreatedAtTag label="Sent at:" createdAt={email.CreatedAt} />
        <div className={cardClasses.fill} />
      </div>
      <Divider />
      <div className={cardClasses.body}>
        <div className={cardClasses.formRow}>
          <span className={cardClasses.label}>Campaign: </span>
          <span className={cardClasses.fill}>{email.CampaignName}</span>
        </div>
        <div className={cardClasses.formRow}>
          <span className={cardClasses.label}>Statuses:</span>
          <span className={cardClasses.tags}>
            {email.OrttoEmailStatusUpdates.map((statusUpdate) => (
              <StatusTag
                key={statusUpdate.ID}
                status={statusUpdate.Status}
                timestamp={statusUpdate.WebhookReceivedAt}
              />
            ))}
          </span>
        </div>
        <div className={cardClasses.formRow}>
          <span className={cardClasses.label}>To:</span>
          <span>{customer.data?.Email ?? ''}</span>
        </div>
        <Divider />
        <Button
          icon="eye-open"
          text="Preview"
          onClick={() => setIsOverlayOpen(true)}
        />
        <Overlay2
          isOpen={isOverlayOpen}
          onClose={() => setIsOverlayOpen(false)}
          canOutsideClickClose
        >
          <div className={styles.center}>
            <Card className={styles.emailPreview}>
              <div className={styles.actionBar}>
                <Button
                  icon={<Icon icon="cross" />}
                  onClick={() => setIsOverlayOpen(false)}
                  minimal
                  small
                />
              </div>
              <EmailHTMLPreview email={email} />
            </Card>
          </div>
        </Overlay2>
      </div>
    </Card>
  );
}
